import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { rid } from "../../utils/shipinhao/utils";
import { ShipinhaoGetMsgParamsSchema } from "./schema";

export type ShipinhaoGetMsgParams = z.infer<typeof ShipinhaoGetMsgParamsSchema>;
export { ShipinhaoGetMsgParamsSchema } from "./schema";

// 原始消息数据结构
export interface RawMessage {
    svrMsgId: string;
    fromUsername: string;
    toUsername: string;
    sessionId: string;
    ts: number;
    textMsg?: {
        content: string;
    };
    imgMsg?: any;
}

// 会话信息数据结构
export interface SessionInfo {
    username: string; // 用户名
    nickname: string; // 昵称
    headImgUrl: string; // 头像URL
    sessionId: string; // 会话ID
    sessionType?: number; // 会话类型 1-用户私信 2-系统消息
    rejectMsg?: number;
    extInfo?: any; // sex: 1-男 2-女
    fromUserType?: number;
    msgList?: RawMessage[]; // 添加消息列表字段
}

// 消息列表响应
export interface MessageListResponse {
    errCode: number;
    errMsg: string;
    data?: {
        msg?: RawMessage[];
    };
}

// 会话详情响应
export interface SessionDetailResponse {
    errCode: number;
    errMsg: string;
    data?: {
        sessionInfo?: SessionInfo[];
    };
}

// 处理后的消息数据
export interface ProcessedMessage {
    platform: string;
    accountId: string;
    messageId: string;
    content: string;
    timestamp: number;
    messageUserId: string;
    messageOwnerUserId: string;
    raw: RawMessage;
}

// 处理后的用户数据
export interface ProcessedMessageUser {
    platform: string;
    accountId: string;
    messageUserId: string;
    messageUserName: string;
    messageUserAvatar: string;
    messageOwnerId: string;
    sessionId: string;
    raw: SessionInfo;
}

export type ShipinhaoGetMsgAction = CommonAction<
    ShipinhaoGetMsgParams,
    {
        sessionInfo: SessionInfo[];
    }
>;

export const shipinhaoGetMsg: ShipinhaoGetMsgAction = async (_task, params) => {
    console.log("视频号获取私信参数", JSON.stringify(params));

    try {
        // 验证 cookies
        if (!params.cookies?.length) {
            return response(414, "视频号获取私信失败：缺少必需的 cookies 参数", {
                sessionInfo: [],
            });
        }

        const { extraParam } = params;
        if (!extraParam?.fingerPrintDeviceId || !extraParam?.aId || !extraParam?.uin) {
            return response(414, "fingerPrintDeviceId、aId和uin不能为空", {
                sessionInfo: [],
            });
        }

        // 获取用户名（finderUsername）
        const finderUserName = extraParam.finderUserName;
        if (!finderUserName) {
            return response(414, "缺少 finderUserName 参数", {
                sessionInfo: [],
            });
        }

        const cookieStr = params.cookies.map((it) => `${it.name}=${it.value}`).join(";");

        const headers = {
            referer: "https://channels.weixin.qq.com/micro/interaction/private_msg",
            cookie: cookieStr,
            Origin: "https://channels.weixin.qq.com",
        };

        const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];
        const http = new Http(...args);

        // 第一步：获取消息历史列表
        const messageListParams = {
            timestamp: Date.now().toString(),
            _log_finder_uin: "",
            _log_finder_id: finderUserName,
            rawKeyBuff: null,
            pluginSessionId: null,
            scene: 7,
            reqScene: 7,
        };

        const messageListUrl = `https://channels.weixin.qq.com/micro/interaction/cgi-bin/mmfinderassistant-bin/private-msg/get-history-msg?_rid=${rid()}`;

        _task.logger.info("请求消息列表:", { url: messageListUrl });

        const messageListRes = await http.api<MessageListResponse>(
            {
                method: "post",
                url: messageListUrl,
                data: messageListParams,
                headers,
            },
            { retries: 3, retryDelay: 20, timeout: 5000 },
        );

        // console.log("消息列表响应", JSON.stringify(messageListRes));

        if (!messageListRes?.data?.msg || messageListRes.data.msg.length === 0) {
            console.log("没有消息数据");
            return response(0, "视频号获取私信成功（无数据）", {
                sessionInfo: [],
            });
        }

        // 收集 sessionId 和消息
        const sessionIds = new Set<string>();
        const allMessages = messageListRes.data.msg;

        for (const msg of allMessages) {
            sessionIds.add(msg.sessionId);
        }

        // 第二步：获取会话详细信息
        const sessionDetailParams = {
            sessionId: Array.from(sessionIds),
            timestamp: Date.now().toString(),
            _log_finder_uin: "",
            _log_finder_id: finderUserName,
            rawKeyBuff: null,
            pluginSessionId: null,
            scene: 7,
            reqScene: 7,
        };

        const sessionDetailUrl = `https://channels.weixin.qq.com/micro/interaction/cgi-bin/mmfinderassistant-bin/private-msg/get-session-info?_rid=${rid()}`;

        _task.logger.info("请求会话详情:", { sessionDetailUrl });

        const sessionDetailRes = await http.api<SessionDetailResponse>(
            {
                method: "post",
                url: sessionDetailUrl,
                data: sessionDetailParams,
                headers,
            },
            { retries: 3, retryDelay: 20, timeout: 5000 },
        );

        // console.log("会话详情响应", JSON.stringify(sessionDetailRes));

        if (sessionDetailRes?.data?.sessionInfo && sessionDetailRes.data.sessionInfo.length > 0) {
            // 遍历每个会话信息，将对应的消息按时间排序后添加到 msgList 字段
            for (const session of sessionDetailRes.data.sessionInfo) {
                // 筛选出该会话的所有消息
                const sessionMessages = allMessages.filter((msg) => msg.sessionId === session.sessionId);

                // 按时间戳降序排序（最新的消息在前）
                sessionMessages.sort((a, b) => b.ts - a.ts);

                // 将排序后的消息列表添加到会话信息中
                session.msgList = sessionMessages;
            }
        }

        const data = {
            sessionInfo: sessionDetailRes.data?.sessionInfo || [],
        };

        console.log("消息列表响应111", JSON.stringify(data));

        return response(0, `视频号获取私信成功${_task.debug ? ` ${http.proxyInfo}` : ""}`, data);
    } catch (error) {
        console.error("获取私信数据异常", error);
        return response(414, `获取私信数据异常: ${error}`, {
            sessionInfo: [],
        });
    }
};
